import {
  aggregateEnabledOrderEditingTools,
  type OrderEditingConfigSource,
} from "@redotech/redo-order-editing-settings-common/order-editing-match";
import { OrderEditingTools } from "@redotech/redo-order-editing-settings-common/order-editing-settings";
import * as z from "zod";

const AllowedOrderEditTypesSchema_ = z.object({
  cancelOrder: z.boolean(),
  changeAddress: z.boolean(),
  updateOrder: z.boolean(),
  addOrderDiscount: z.boolean(),
  addOrderGiftNote: z.boolean(),
});

export const AllowedOrderEditTypesSchema: z.ZodType<AllowedOrderEditTypes> =
  AllowedOrderEditTypesSchema_;
export interface AllowedOrderEditTypes extends z.infer<
  typeof AllowedOrderEditTypesSchema_
> {}

const OrderEditingSettingsSchema_ = z.object({
  cancelOrder: z.object({ enabled: z.boolean() }),
  updateOrder: z.object({
    enabled: z.boolean(),
    addItem: z.boolean(),
    removeItem: z.boolean(),
    changeVariant: z.boolean(),
  }),
  changeAddress: z.object({ enabled: z.boolean() }),
  addOrderDiscount: z.object({ enabled: z.boolean() }),
});

export const OrderEditingSettingsSchema: z.ZodType<OrderEditingSettings> =
  OrderEditingSettingsSchema_;
export interface OrderEditingSettings extends z.infer<
  typeof OrderEditingSettingsSchema_
> {}

export const OrderEditingToolsDefault: OrderEditingTools = {
  cancelOrder: {
    enabled: false,
    cancelOrderTimeWindowAfterPlacingMinutes: 60 * 24,
    requireConfirmation: true,
    restockInventory: true,
  },
  changeAddress: {
    enabled: false,
    changeOrderAddressTimeWindowAfterPlacingMinutes: 60 * 24,
    requireConfirmation: true,
  },
  addOrderDiscount: {
    enabled: false,
    addOrderDiscountTimeWindowAfterPlacingMinutes: 60 * 24,
  },
  addOrderGiftNote: {
    enabled: false,
    addOrderGiftNoteTimeWindowAfterPlacingMinutes: 60 * 24,
  },
  updateOrder: {
    enabled: false,
    updateOrderTimeWindowAfterPlacingMinutes: 60 * 24,
    addItem: false,
    removeItem: false,
    changeVariant: false,
    removeItemRestockInventory: true,
    changeVariantRestockInventory: true,
  },
};

// TODO(Connor Ellis): teams without rules get default windows, not their
// legacy `tools` windows; getConciergeSkillsSettings shows and writes these.
/**
 * Default tool settings with each action's `enabled` flag set when any rule
 * enables it. For team-level consumers that have no order to resolve a rule
 * against; time windows and confirmation flags are defaults, not per-rule.
 */
export function getTeamLevelOrderEditingTools(
  settings: OrderEditingConfigSource | null | undefined,
): OrderEditingTools {
  const enabled = aggregateEnabledOrderEditingTools(settings);
  const defaults = OrderEditingToolsDefault;
  return {
    cancelOrder: { ...defaults.cancelOrder, enabled: enabled.cancelOrder },
    changeAddress: {
      ...defaults.changeAddress,
      enabled: enabled.changeAddress,
    },
    addOrderDiscount: {
      ...defaults.addOrderDiscount,
      enabled: enabled.addOrderDiscount,
    },
    addOrderGiftNote: {
      ...defaults.addOrderGiftNote,
      enabled: enabled.addOrderGiftNote,
    },
    updateOrder: { ...defaults.updateOrder, ...enabled.updateOrder },
  };
}
